import { HpApiApartmentReviewsV3Handler_apartmentReviews_Path } from '@zg-rentals/ts-hotpads-api-client';
import type { HpApartmentReviewsRequestDto, HpApartmentReviewsResponseDto, HpApiResponseDto, RedditReviewTopicDto } from '@zg-rentals/ts-hotpads-api-client';
import type { DeepPartial, Wire } from '../wire';
import { apiClient } from '../client/apiClient';

export type ApartmentReviews = Omit<DeepPartial<Wire<HpApartmentReviewsResponseDto>>, 'reddit'> & {
  reddit?: { topics: Array<Wire<RedditReviewTopicDto>> };
};

export type BuildingReviewsSnapshot = {
  lotId: string;
  reviews: ApartmentReviews | null;
  updatedAt: number;
};

export const APARTMENT_REVIEWS_TIMEOUT_MS = 1_500;
export const APARTMENT_REVIEWS_STALE_TIME_MS = 5 * 60 * 1000;

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const count = (value: unknown): value is number => finite(value) && Number.isInteger(value) && value >= 0;
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

export function normalizeApartmentReviews(value: unknown): ApartmentReviews | null {
  const raw = record(value);
  if (!raw) return null;
  const reviews: ApartmentReviews = {};
  const reputation = record(raw.reputation);
  if (reputation) {
    const categories = Array.isArray(reputation.categories) ? reputation.categories.flatMap((item: unknown) => {
      const category = record(item);
      if (!category || !nonempty(category.category)) return [];
      return [{
        category: category.category,
        ...(nonempty(category.summary) ? { summary: category.summary } : {}),
        ...(count(category.reviewCount) ? { reviewCount: category.reviewCount } : {}),
        ...(finite(category.score) ? { score: category.score } : {}),
      }];
    }) : [];
    const averageRating = reputation.averageRating;
    const totalReviews = reputation.totalReviews;
    const validRating = finite(averageRating) && averageRating >= 0 && averageRating <= 5;
    if (validRating || count(totalReviews) || categories.length) {
      reviews.reputation = {
        ...(validRating ? { averageRating } : {}),
        ...(count(totalReviews) ? { totalReviews } : {}),
        ...(categories.length ? { categories } : {}),
      };
    }
  }
  const google = record(raw.google);
  if (nonempty(google?.placeId)) reviews.google = { placeId: google.placeId.trim() };
  const reddit = record(raw.reddit);
  const topics = Array.isArray(reddit?.topics) ? reddit.topics.flatMap((item: unknown) => {
    const link = record(item);
    if (!link || !nonempty(link.topic) || !nonempty(link.url) || !nonempty(link.title) || !nonempty(link.description)) return [];
    try {
      const url = new URL(link.url);
      if (url.protocol !== 'https:' || !['reddit.com', 'www.reddit.com'].includes(url.hostname) || url.username || url.password) return [];
      return [{ topic: link.topic, url: link.url, title: link.title, description: link.description, icon: nonempty(link.icon) ? link.icon : 'unknown', tone: nonempty(link.tone) ? link.tone : 'neutral' }];
    } catch { return []; }
  }) : [];
  if (topics.length) reviews.reddit = { topics };
  return Object.keys(reviews).length ? reviews : null;
}

export async function fetchBuildingReviews(lotId: string, signal?: AbortSignal): Promise<ApartmentReviews | null> {
  const body: HpApartmentReviewsRequestDto = { maloneLotIdEncoded: lotId };
  const response = await apiClient<DeepPartial<Wire<HpApiResponseDto<HpApartmentReviewsResponseDto>>>>({
    // Retain request-scoped SSR forwarding and cancellation; the generated default client is browser-oriented.
    path: HpApiApartmentReviewsV3Handler_apartmentReviews_Path.replace(/^\/api/, ''),
    method: 'POST',
    body,
    signal,
    timeout: APARTMENT_REVIEWS_TIMEOUT_MS,
  });
  return normalizeApartmentReviews(response.data);
}
